import { useEffect, useMemo, useState } from 'react';
import { Card, ConfirmModal, Field, Skeleton } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { deleteMaster, fetchBudgets, listenAccounts, saveMaster } from '../../firebase/repo';
import { DEFAULT_DROPDOWNS, DROPDOWN_GROUPS, normalizeDropdownValue } from '../../types/dropdowns';
import type { Account, Budget, Contact, MasterItem, MasterType } from '../../types';
import { ContactManager } from '../contacts/ContactManager';
import { BudgetManager } from '../budgets/BudgetManager';
import { AccountManager } from '../accounts/AccountManager';
import { NotificationDeliverySettings } from '../notifications/NotificationDeliverySettings';
import { monthKey, todayISO } from '../../utils/date';
import { buildSettingsSummary, resolveSettingsSection, settingsSectionHash, type SettingsSection } from '../../utils/settings';

const categoryGroups: MasterType[] = [
  DROPDOWN_GROUPS.DAILY_CATEGORY,
  DROPDOWN_GROUPS.INCOME_CATEGORY,
  DROPDOWN_GROUPS.INVESTMENT_CATEGORY,
  DROPDOWN_GROUPS.PLAN_CATEGORY
];

const paymentGroups: MasterType[] = [DROPDOWN_GROUPS.PAYMENT_MODE];
const planGroups: MasterType[] = [DROPDOWN_GROUPS.PLAN_CATEGORY, DROPDOWN_GROUPS.PLAN_FREQUENCY, DROPDOWN_GROUPS.FLOW, DROPDOWN_GROUPS.CRDR_TYPE, DROPDOWN_GROUPS.TAX_TAG];

export function SetupPage({ uid, email = '', masters, contacts, grouped }: { uid: string; email?: string; masters: MasterItem[]; contacts: Contact[]; grouped: Record<MasterType, string[]> }) {
  const toast = useToast();
  const currentMonth = monthKey(todayISO());
  const [section, setSection] = useState<SettingsSection>(() => resolveSettingsSection(typeof window === 'undefined' ? '' : window.location.hash));
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loadingHub, setLoadingHub] = useState(true);

  useEffect(() => {
    const syncSection = () => setSection(resolveSettingsSection(window.location.hash));
    window.addEventListener('hashchange', syncSection);
    window.addEventListener('popstate', syncSection);
    return () => {
      window.removeEventListener('hashchange', syncSection);
      window.removeEventListener('popstate', syncSection);
    };
  }, []);

  useEffect(() => listenAccounts(uid, (rows) => {
    setAccounts(rows);
    setLoadingHub(false);
  }), [uid]);

  useEffect(() => {
    let active = true;
    fetchBudgets(uid, currentMonth)
      .then((rows) => { if (active) setBudgets(rows); })
      .catch((error) => toast(error instanceof Error ? error.message : 'Budget summary load failed', 'error'));
    return () => { active = false; };
  }, [uid, currentMonth, toast]);

  const summary = useMemo(() => buildSettingsSummary({ masters, contacts, accounts, budgets, monthKey: currentMonth }), [masters, contacts, accounts, budgets, currentMonth]);

  const openSection = (next: SettingsSection) => {
    setSection(next);
    if (typeof window !== 'undefined') window.history.pushState(null, '', settingsSectionHash(next));
  };

  const backToHub = () => openSection('hub');

  return <div className="page setup-page settings-page">
    {section === 'hub' && <SettingsHub summary={summary} loading={loadingHub} onOpen={openSection} />}
    {section !== 'hub' && <div className="settings-detail-shell">
      <button className="btn small" onClick={backToHub}>Back to settings</button>
      {section === 'categories' && <MasterManager uid={uid} title="Categories" description="Expense, income, investment, and plan categories used across forms." masters={masters} groups={categoryGroups} defaultGroup={DROPDOWN_GROUPS.DAILY_CATEGORY} />}
      {section === 'paymentModes' && <MasterManager uid={uid} title="Payment modes" description="UPI, cash, card, bank, and other modes shown in payment fields." masters={masters} groups={paymentGroups} defaultGroup={DROPDOWN_GROUPS.PAYMENT_MODE} />}
      {section === 'planSettings' && <MasterManager uid={uid} title="Plan and ledger dropdowns" description="Frequencies, flows, CR/DR types, and report tags." masters={masters} groups={planGroups} defaultGroup={DROPDOWN_GROUPS.PLAN_CATEGORY} />}
      {section === 'contacts' && <ContactManager uid={uid} contacts={contacts} />}
      {section === 'budgets' && <BudgetManager uid={uid} grouped={grouped} />}
      {section === 'accounts' && <AccountManager uid={uid} accounts={accounts} />}
      {section === 'notifications' && <NotificationDeliverySettings uid={uid} email={email} />}
    </div>}
  </div>;
}

function SettingsHub({ summary, loading, onOpen }: {
  summary: ReturnType<typeof buildSettingsSummary>;
  loading: boolean;
  onOpen: (section: SettingsSection) => void;
}) {
  const cards = [
    { key: 'accounts' as const, title: 'Accounts & Cards', meta: `${summary.accounts} active / ${summary.creditCards} cards`, detail: 'Balances, card limits, transfers, and ledgers' },
    { key: 'categories' as const, title: 'Categories', meta: `${summary.categories} active`, detail: 'Daily, income, investment, and plan categories' },
    { key: 'paymentModes' as const, title: 'Payment Modes', meta: `${summary.paymentModes} active`, detail: 'Modes used while saving transactions and plan payments' },
    { key: 'contacts' as const, title: 'Contacts', meta: `${summary.contacts} active / ${summary.googleContacts} Google`, detail: 'People for CR/DR ledger and contact suggestions' },
    { key: 'budgets' as const, title: 'Budgets', meta: `${summary.budgets} for this month`, detail: 'Monthly category limits and overspending alerts' },
    { key: 'notifications' as const, title: 'Notifications', meta: 'Push / Email', detail: 'Browser push, email, privacy, and quiet hours' },
    { key: 'planSettings' as const, title: 'Plan Settings', meta: `${summary.planSettings} active`, detail: 'Plan frequency, flow, CR/DR, and tax tag dropdowns' }
  ];

  return <>
    <Card className="settings-hero">
      <div className="section-title">
        <div><p className="eyebrow">Settings</p><h2>Setup center</h2><p className="muted">Manage the masters that power transactions, ledgers, reports, and alerts.</p></div>
        <span className="pill">Safe edit</span>
      </div>
      {loading ? <Skeleton /> : <div className="settings-summary-strip">
        <span><b>{summary.accounts}</b> accounts</span>
        <span><b>{summary.categories}</b> categories</span>
        <span><b>{summary.contacts}</b> contacts</span>
        <span><b>{summary.budgets}</b> budgets</span>
      </div>}
      {!!summary.warnings.length && <div className="settings-warning-list">{summary.warnings.map((warning) => <p key={warning}>{warning}</p>)}</div>}
    </Card>
    <div className="settings-card-grid">
      {cards.map((card) => <button className="settings-card" key={card.key} onClick={() => onOpen(card.key)}>
        <span>{card.title}</span>
        <b>{card.meta}</b>
        <small>{card.detail}</small>
      </button>)}
    </div>
  </>;
}

function MasterManager({ uid, title, description, masters, groups, defaultGroup }: {
  uid: string;
  title: string;
  description: string;
  masters: MasterItem[];
  groups: MasterType[];
  defaultGroup: MasterType;
}) {
  const toast = useToast();
  const [group, setGroup] = useState<MasterType>(defaultGroup);
  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [editing, setEditing] = useState<MasterItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [savingDropdown, setSavingDropdown] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MasterItem | null>(null);

  useEffect(() => {
    setGroup(defaultGroup);
    setEditing(null);
    setValue('');
    setFormOpen(false);
  }, [defaultGroup]);

  const allRows = useMemo(
    () => masters.filter((m) => groups.includes((m.group || m.type) as MasterType)).sort((a, b) => (mGroup(a)).localeCompare(mGroup(b)) || a.sortOrder - b.sortOrder || a.value.localeCompare(b.value)),
    [groups, masters]
  );

  const groupRows = useMemo(() => allRows.filter((m) => mGroup(m) === group), [allRows, group]);
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows
      .filter((row) => activeFilter === 'all' || (activeFilter === 'active' ? row.active !== false : row.active === false))
      .filter((row) => !q || [row.value, mGroup(row)].some((part) => part.toLowerCase().includes(q)));
  }, [activeFilter, allRows, query]);

  const startEdit = (item: MasterItem) => {
    setEditing(item);
    setGroup(mGroup(item) as MasterType);
    setValue(item.value);
    setFormOpen(true);
  };

  const startAdd = () => {
    setEditing(null);
    setValue('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setEditing(null);
    setValue('');
    setFormOpen(false);
  };

  const addMaster = async () => {
    const cleanValue = value.trim().replace(/\s+/g, ' ');
    if (!cleanValue) return toast('Dropdown value required');
    const duplicate = groupRows.some((m) => m.id !== editing?.id && normalizeDropdownValue(m.value) === normalizeDropdownValue(cleanValue));
    if (duplicate) return toast(`${cleanValue} already exists in ${group}`);
    setSavingDropdown(true);
    try {
      await saveMaster(uid, {
        ...(editing || {}),
        type: group,
        group,
        value: cleanValue,
        active: editing?.active ?? true,
        sortOrder: editing?.sortOrder || groupRows.length + 1
      });
      closeForm();
      toast(editing ? 'Dropdown updated' : 'Dropdown saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Dropdown save failed', 'error');
    } finally {
      setSavingDropdown(false);
    }
  };

  const toggleMaster = async (item: MasterItem) => {
    await saveMaster(uid, { ...item, active: !item.active });
    toast(item.active ? 'Dropdown deactivated' : 'Dropdown activated');
  };

  const remove = async () => {
    if (!deleteTarget || savingDropdown) return;
    setSavingDropdown(true);
    try {
      await deleteMaster(uid, deleteTarget.id);
      setDeleteTarget(null);
      toast('Dropdown deleted');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Dropdown delete failed', 'error');
    } finally {
      setSavingDropdown(false);
    }
  };

  return <Card className="settings-manager-card">
    <div className="section-title">
      <div><h2>{title}</h2><p className="muted">{description}</p></div>
      <button className="btn small primary" onClick={startAdd}>Add value</button>
    </div>
    {formOpen && <div className="settings-form-panel">
      <div className="section-title compact"><div><h3>{editing ? 'Edit dropdown value' : 'New dropdown value'}</h3><p className="muted">Duplicates are blocked inside the same group.</p></div><button className="btn small" onClick={closeForm}>Close</button></div>
      <div className="grid2">
        <Field label="Group">
          <select value={group} onChange={(e) => setGroup(e.target.value as MasterType)}>
            {groups.map((x) => <option key={x}>{x}</option>)}
          </select>
        </Field>
        <Field label={editing ? 'Edit value' : 'New value'}>
          <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Add dropdown value" />
        </Field>
      </div>
      <button className="btn primary wide" disabled={savingDropdown} onClick={addMaster}>{savingDropdown ? 'Saving...' : editing ? 'Update value' : 'Save value'}</button>
    </div>}
    <div className="settings-manager-toolbar">
      <Field label="Search"><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search saved values" /></Field>
      <Field label="Status"><select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as typeof activeFilter)}><option value="all">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select></Field>
    </div>
    <div className="settings-summary-strip">
      <span><b>{allRows.filter((m) => m.active !== false).length}</b> active</span>
      <span><b>{allRows.length}</b> saved</span>
      <span><b>{groups.length}</b> groups</span>
    </div>
    {!visibleRows.length && <p className="empty-state">No saved values found. Defaults for {group}: {DEFAULT_DROPDOWNS[group].join(', ')}</p>}
    {visibleRows.map((m) => <div className="master-row" key={m.id}>
      <div>
        <b>{m.value}</b>
        <span>{m.group || m.type}</span>
      </div>
      <div className="row-actions">
        <button className={`status-pill ${m.active ? 'active' : 'inactive'}`} onClick={() => toggleMaster(m)}>{m.active ? 'Active' : 'Inactive'}</button>
        <details className="overflow-menu"><summary>More</summary><div><button className="btn small" onClick={() => startEdit(m)}>Edit</button><button className="btn small danger" onClick={() => setDeleteTarget(m)}>Delete</button></div></details>
      </div>
    </div>)}
    <ConfirmModal open={!!deleteTarget} title="Delete dropdown value?" message="This removes the value from future dropdowns. Existing saved records keep their stored text." tone="danger" confirmLabel="Delete" loading={savingDropdown} details={deleteTarget ? [{ label: 'Group', value: mGroup(deleteTarget) }, { label: 'Value', value: deleteTarget.value }] : []} onCancel={() => setDeleteTarget(null)} onConfirm={remove} />
  </Card>;
}

function mGroup(item: MasterItem) {
  return item.group || item.type;
}
